import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import AuthService from '../services/authService.js';

// Initialize auth service
const authService = new AuthService();

// Hardcoded user credentials for demo
const USERS = [
  { id: 'user1', user: 'user1', email: 'user1@example.com', password: 'pass1' },
  { id: 'user2', user: 'user2', email: 'user2@example.com', password: 'pass2' }
];

export const useAuthStore = create(
  persist(
    (set) => ({
      currentUser: null,
      isAuthenticated: false,
      currentDeviceId: null, // Store device ID for current session

      login: async (email, password, recaptchaToken) => {
        // Use the API service for signin
        const response = await authService.signin(email, password, recaptchaToken);

        console.log('🔍 Login Response Debug:', response);
        console.log('🔐 Device ID in response:', response.deviceId);

        try {

          if (response && response.success && response.data?.statusCode === 200) {
            // Extract user data from response.data.data
            const apiData = response.data.data;
            const userData = {
              id: apiData?.userId,
              email: apiData?.userEmailId || email,
              token: apiData?.token,
              is_profile_set: apiData?.is_profile_set,
              lastLogoutTime: apiData?.lastLogoutTime,
              ...apiData
            };

            console.log('✅ Login successful, setting user data:', userData);
            console.log('🔍 is_profile_set from API:', apiData?.is_profile_set, typeof apiData?.is_profile_set);

            // Explicitly persist deviceId to localStorage
            if (response.deviceId) {
              localStorage.setItem('deviceId', response.deviceId);
              console.log('🔐 Persisted device ID to localStorage:', response.deviceId);
            }

            set({
              currentUser: userData,
              isAuthenticated: true,
              currentDeviceId: response.deviceId || null // Store device ID for logout
            });

            console.log('🔐 Stored device ID for session:', response.deviceId);
            console.log('🔍 Auth store state after login:', {
              hasUser: !!userData,
              hasDeviceId: !!response.deviceId,
              deviceId: response.deviceId,
              userEmail: userData?.email
            });

            // Return success with profile status for navigation decision
            return {
              success: true,
              is_profile_set: apiData?.is_profile_set
            };
          }

          console.log('❌ Login failed - invalid response structure');
          return { success: false };
        } catch (error) {
          console.error('Login processing failed:', error);
        }

        // Check if we should use fallback users (API call failed or returned unsuccessful)
        if (!response.success) {
          console.log('🔄 API call unsuccessful, checking fallback users');

          const user = USERS.find(
            (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
          );

          if (user) {
            console.log('🔄 Using fallback hardcoded user');

            // Use device ID from the API response (it should always be there now)
            const deviceIdFromApiCall = response.deviceId;

            if (deviceIdFromApiCall) {
              console.log('🔐 Using device ID from API call for fallback user:', deviceIdFromApiCall);
              console.log('✅ Device ID consistency maintained between signin and signout');
            } else {
              console.warn('⚠️ WARNING: No device ID found in API response, this should not happen');
            }

            set({
              currentUser: user,
              isAuthenticated: true,
              currentDeviceId: deviceIdFromApiCall
            });

            return { success: true, is_profile_set: true }; // Assume profile is set for demo users
          }
        }

        return { success: false };
      },

      thirdPartyLogin: async (firebaseUser) => {
        try {
          console.log('🔍 Firebase user data:', firebaseUser);

          // Import helper functions dynamically to avoid circular dependencies
          const { extractSocialLoginData, getDeviceType, getClientIP, getClientLocation } = await import('../utils/helpers.js');

          // Extract social login data from Firebase user
          const socialData = extractSocialLoginData(firebaseUser);
          console.log('🔍 Extracted social data:', socialData);

          // Get client information
          const device = getDeviceType();
          const ipAddress = await getClientIP();
          const location = await getClientLocation();

          // Call backend API for social login
          const response = await authService.socialLogin(
            socialData.email,
            socialData.providerName,
            socialData.socialUserId,
            device,
            ipAddress,
            location
          );

          console.log('🔍 Social Login API Response:', response);
          console.log('🔍 Response validation:', {
            hasResponse: !!response,
            hasSuccess: response?.success,
            hasData: !!response?.data,
            statusCode: response?.data?.statusCode,
            isProfileSet: response?.data?.data?.is_profile_set
          });

          if (response && response.success && response.data?.statusCode === 200) {
            console.log('✅ API call successful, processing response...');
            // Extract user data from response.data.data
            const apiData = response.data.data;
            console.log('🔍 API Data:', apiData);
            console.log('🔍 is_profile_set from API:', apiData?.is_profile_set, typeof apiData?.is_profile_set);

            const userData = {
              id: apiData?.userId,
              email: apiData?.userEmailId || socialData.email,
              token: apiData?.token,
              is_profile_set: apiData?.is_profile_set,
              // Keep Firebase user data for display purposes
              displayName: firebaseUser.displayName || firebaseUser.providerData?.[0]?.displayName,
              photoURL: firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL,
              providerData: firebaseUser.providerData,
              ...apiData
            };

            console.log('✅ Social login successful, setting user data:', userData);

            set({
              currentUser: userData,
              isAuthenticated: true,
              currentDeviceId: response.deviceId || null // Store device ID for logout
            });

            console.log('🔐 Stored device ID for social login session:', response.deviceId);

            // Return user data with profile status for navigation decision
            const returnValue = {
              success: true,
              is_profile_set: apiData?.is_profile_set
            };
            console.log('🔍 Returning to LoginPage:', returnValue);
            return returnValue;
          }

          console.log('❌ Social login failed - invalid response structure');
          return { success: false };
        } catch (error) {
          console.error('❌ Social login API call failed:', error);

          // Fallback to Firebase-only authentication for development
          console.log('🔄 Using fallback Firebase-only authentication (API failed)');
          set({
            currentUser: {
              id: firebaseUser.uid,
              email: firebaseUser.email || firebaseUser.providerData?.[0]?.email,
              displayName: firebaseUser.displayName || firebaseUser.providerData?.[0]?.displayName,
              photoURL: firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL,
              providerData: firebaseUser.providerData,
              is_profile_set: true // Assume profile is set for Firebase-only fallback
            },
            isAuthenticated: true
          });
          return { success: true, is_profile_set: true };
        }
      },

      logout: async () => {
        console.log('🔄 Starting API-first logout process');

        // Get current user email and device ID for signout API call
        const state = useAuthStore.getState();
        const userEmail = state.currentUser?.email;
        const deviceId = state.currentDeviceId;

        console.log('🔍 Logout state check:', {
          hasUser: !!state.currentUser,
          userEmail,
          hasDeviceId: !!deviceId,
          deviceId,
          fullState: {
            isAuthenticated: state.isAuthenticated,
            currentDeviceId: state.currentDeviceId
          }
        });

        if (!userEmail) {
          console.error('❌ Cannot logout: No user email found');
          throw new Error('Cannot logout: User email not available');
        }

        if (!deviceId) {
          console.warn('⚠️ No stored device ID found for logout - this may indicate a session from before device tracking was implemented');

          // Generate a device ID for this logout attempt as fallback
          const { generateDeviceIdWithTimestamp } = await import('../utils/deviceHelper.js');
          const fallbackDeviceId = generateDeviceIdWithTimestamp();

          console.log('🔄 Using fallback device ID for logout:', fallbackDeviceId);

          // Update the stored device ID for consistency
          set((state) => ({ ...state, currentDeviceId: fallbackDeviceId }));

          // Use the fallback device ID
          deviceId = fallbackDeviceId;
        }

        try {
          console.log('📧 Calling signout API with stored device ID:', { userEmail, deviceId });

          // Call signout API with stored device ID - MUST succeed before local cleanup
          const result = await authService.signout(userEmail, deviceId);

          if (!result.success) {
            console.error('❌ Signout API failed:', result.error);
            throw new Error(result.error || 'Signout API call failed');
          }

          console.log('✅ Signout API succeeded, proceeding with local cleanup');

          // Import clearAuthStorage dynamically to avoid circular dependencies
          const { clearAuthStorage } = await import('../utils/authValidation.js');

          // Clear all authentication storage after successful API call
          clearAuthStorage(true, true); // Pass apiLogoutSucceeded = true

          // Clear API client token
          await authService.apiClient.setAuthToken(null);

          // Clear local state only after successful API logout
          set({ currentUser: null, isAuthenticated: false, currentDeviceId: null });

          console.log('✅ Logout completed successfully - frontend and backend synchronized');

          return { success: true };

        } catch (error) {
          console.error('❌ Logout failed - keeping user logged in locally:', error);

          // Do NOT clear local state if API call fails
          // This maintains synchronization between frontend and backend
          throw error; // Re-throw to let calling code handle the error
        }
      },



      // Set authentication state for traditional signup auto-signin
      setAuthenticationState: async (userData) => {
        console.log('🔐 Setting authentication state for traditional signup:', userData);

        // Generate device ID if not provided (for traditional signup flow)
        let deviceId = userData.deviceId;
        if (!deviceId) {
          const { generateDeviceIdWithTimestamp } = await import('../utils/deviceHelper.js');
          deviceId = generateDeviceIdWithTimestamp();
          console.log('🔐 Generated device ID for traditional signup:', deviceId);
        }

        set({
          currentUser: userData,
          isAuthenticated: true,
          currentDeviceId: deviceId
        });
      },

      // Update profile completion status
      updateProfileStatus: (isProfileSet) => {
        console.log('🔄 Updating profile completion status:', isProfileSet);
        set((state) => ({
          currentUser: state.currentUser ? {
            ...state.currentUser,
            is_profile_set: isProfileSet
          } : null
        }));
      },

      // Get profile completion status
      isProfileComplete: () => {
        const state = useAuthStore.getState();
        return state.currentUser?.is_profile_set === true;
      }
    }),
    { name: 'auth-storage' }
  )
);
